import { AudioManager, defaultAudioConfig, type AudioConfig } from './AudioManager';
import { SoundCategory, SOUND_CATEGORY_ORDER } from '@shared/data/sounds';

/**
 * Instancia ÚNICA de `AudioManager` compartida entre el **boot/login** (música de intro/createchar,
 * antes de que exista GameScene) y el **juego** (música por zona + todos los SFX). Un solo
 * AudioContext ⇒ handoff sin cortes (el `playMusic` de la zona crossfadea desde la intro) y config de
 * audio consistente entre el botón de música del loading y Opciones in-game. Ver docs/SOUND_AND_MUSIC.md.
 */

// Clave de localStorage de las opciones del cliente (misma que usa GameScene/PersistenceController).
// Se duplica el literal a propósito para no importar GameScene desde el boot (evita arrastrar Phaser).
const CLIENT_OPTIONS_STORAGE_KEY = 'aureum_game_options_v1';

interface SavedAudioBlob {
  masterVolume?: number;
  categories?: Record<string, { enabled?: boolean; volume?: number }>;
  musicLowHpSpeedup?: boolean;
}

let instance: AudioManager | null = null;

/** Devuelve la instancia compartida (la crea perezosamente). */
export function getAudioManager(): AudioManager {
  if (!instance) instance = new AudioManager();
  return instance;
}

/** Lee las opciones de audio guardadas en localStorage (o null). */
function readSavedAudio(): SavedAudioBlob | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CLIENT_OPTIONS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { audio?: SavedAudioBlob };
    return parsed.audio ?? null;
  } catch {
    return null;
  }
}

/** Construye un AudioConfig a partir de lo guardado (o defaults) y lo aplica al manager. */
export function applyPersistedAudioConfig(mgr: AudioManager): void {
  const config: AudioConfig = defaultAudioConfig();
  const saved = readSavedAudio();
  if (saved) {
    if (typeof saved.masterVolume === 'number') config.masterVolume = saved.masterVolume;
    if (typeof saved.musicLowHpSpeedup === 'boolean') config.musicLowHpSpeedup = saved.musicLowHpSpeedup;
    if (saved.categories) {
      for (const cat of SOUND_CATEGORY_ORDER) {
        const s = saved.categories[cat];
        if (s) {
          config.categories[cat] = {
            enabled: s.enabled !== false,
            volume: typeof s.volume === 'number' ? clamp01(s.volume) : 1,
          };
        }
      }
    }
  }
  mgr.applyConfig(config);
}

/** True si el canal Música está habilitado según lo guardado (default true). */
export function isMusicEnabledPersisted(): boolean {
  return readSavedAudio()?.categories?.[SoundCategory.MUSIC]?.enabled !== false;
}

/**
 * Persiste SÓLO el enabled del canal Música en el blob de opciones (merge, sin pisar el resto). Lo usa
 * el botón de música del loading para que el toggle sobreviva a recargas y coincida con Opciones.
 */
export function persistMusicEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem(CLIENT_OPTIONS_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as { audio?: SavedAudioBlob }) : {};
    const audio: SavedAudioBlob = parsed.audio ?? {};
    const categories = audio.categories ?? {};
    const music = categories[SoundCategory.MUSIC] ?? {};
    categories[SoundCategory.MUSIC] = { ...music, enabled };
    audio.categories = categories;
    const next = { ...(parsed as object), audio };
    window.localStorage.setItem(CLIENT_OPTIONS_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignorar errores de storage
  }
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}
